import { createContext, useContext, useMemo, useRef, useState } from 'react';

import type { ModalKey } from '@/modals/keys';
import { modalRegistry } from '@/modals/registry';
import type { ConfirmMessage } from '@/models/ConfirmMessage';
import useAppUtils from '@/utils/appUtils';

import { VNBaseModal } from './VNBaseModal';

export type ModalConfig = {
  title: React.ReactNode;
  size: 'sm' | 'md' | 'lg' | 'xl';
  node: React.ReactNode;
  id?: string | null;
  fullscreen?: boolean;
  cssClassName?: string;
  onCloseCallback?: (meta?: Record<string, any>) => void;
};

const defaultModal: ModalConfig = {
  title: 'modal',
  size: 'sm',
  node: null,
  id: null,
  fullscreen: false,
  cssClassName: null,
  onCloseCallback: undefined,
};

interface ModalContextProps {
  onOpen: (
    title: string,
    size: string,
    node: React.ReactNode,
    id?: string,
    fullscreen?: boolean,
    options?: {
      id?: string;
      onCloseCallback?: (meta?: Record<string, any>) => void;
    }
  ) => void;
  onClose: () => void;
  updateTitle: (title: React.ReactNode) => void;
  updateModalSize: (size: 'sm' | 'md' | 'lg' | 'xl') => void;
  openByKey: (key: ModalKey, props?: any) => void;
  setPreventClose: (prevent: boolean, message?: ConfirmMessage) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalContext.Provider');
  }
  return context;
};

export const VNModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalArgs, setModalArgs] = useState<ModalConfig>(defaultModal);
  const onCloseCallbackRef = useRef<((meta?: Record<string, any>) => void) | undefined>(undefined);
  const { vnConfirm } = useAppUtils();

  // Ref to track if the current modal content wants to prevent closing
  const preventionRef = useRef<{ shouldPrevent: boolean; message?: ConfirmMessage }>({
    shouldPrevent: false,
  });

  const values = useMemo(
    () => ({
      onOpen: (title: string, size: 'sm' | 'md' | 'lg' | 'xl', node: React.ReactNode, id: string, options = {}) => {
        setModalArgs({
          title,
          size,
          node,
          id,
        });
        setOpenModal(true);
      },
      setPreventClose: (prevent: boolean, message?: ConfirmMessage) => {
        preventionRef.current = { shouldPrevent: prevent, message };
      },
      onClose: async (options?: { force?: boolean }) => {
        const { shouldPrevent, message } = preventionRef.current;

        // Check if we should alert the user
        if (shouldPrevent) {
          const confirmed = await vnConfirm(message);
          if (!confirmed) return;
        }

        // Clean up and Close
        setOpenModal(false);
        setModalArgs(defaultModal);
        preventionRef.current = { shouldPrevent: false };

        if (onCloseCallbackRef.current) {
          onCloseCallbackRef.current();
          onCloseCallbackRef.current = undefined;
        }
      },
      updateTitle: (title: React.ReactNode) => {
        setModalArgs((prev) => ({
          ...prev,
          title,
        }));
      },
      updateModalSize: (size: 'sm' | 'md' | 'lg' | 'xl') => {
        setModalArgs((prev) => ({
          ...prev,
          size,
        }));
      },
      openByKey: (key: ModalKey, props?: any) => {
        const factory = modalRegistry[key];
        if (!factory) {
          console.error(`Modal not registered: ${key}`);
          return;
        }

        const config = factory(props);

        if (!config || !config.node) {
          console.error(`Modal config invalid for key: ${key}`, config);
          return;
        }

        // console.log({ config });
        // Support onCloseCallback from openByKey configs too
        onCloseCallbackRef.current = config.onCloseCallback;
        setModalArgs({
          title: config.title,
          size: config.size ?? 'sm',
          node: config.node,
          id: config.id ?? null,
          fullscreen: config.fullscreen ?? false,
          cssClassName: config.cssClassName ?? '',
        });

        setOpenModal(true);
      },
      // registerActions: (actions: ModalActionConfig) => {
      //   setModalArgs((prev) => ({ ...prev, actions }));
      // },
    }),
    [vnConfirm]
  );

  return (
    <ModalContext.Provider value={values}>
      {children}

      {openModal && (
        <VNBaseModal
          open={openModal}
          title={modalArgs.title as string}
          width={modalArgs.size}
          onClose={values.onClose}
          cssClassName={modalArgs.cssClassName}
          id={modalArgs.id}
          fullscreen={modalArgs.fullscreen ?? false}
        >
          {modalArgs.node}
        </VNBaseModal>
      )}
    </ModalContext.Provider>
  );
};
